import Swal from "sweetalert2";
import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Card, Table, Button, Form, OverlayTrigger, Tooltip, Badge, Spinner, Offcanvas, Modal, ProgressBar } from 'react-bootstrap';
import LayoutDashboard from "layouts/layout-dashboard-collapse";
import dayjs from 'dayjs';
import 'dayjs/locale/id';
import { getRequest, postRequest, patchRequest, deleteRequest } from 'services/api';
import { buildQueryString } from "utils/queryHelpers";
import { useSearchParams } from "react-router-dom";
import { handleExcelGridKeyDown } from "./excel-grid-navigation";
import { buildBulkFailureHtml, getBulkFailures, runChunkedBulkRequest } from "./bulk-api";
import * as XLSX from "xlsx";
import "./accounting-v2.css";
import { getAllMasterKeep } from "./master-keep-api";

const formatPrice = (value) =>
  Number(value || 0).toLocaleString('id-ID', { maximumFractionDigits: 0 });

const parseCurrency = (value) =>
  Number(String(value ?? '').replace(/\./g, '').replace(/[^0-9-]/g, '')) || 0;

// Sel angka di Keep dengan cara kerja seperti Excel (sama seperti tabel Trf di Pembukuan):
// fokus = isi terblok (ketikan pertama mengganti), angka mentah saat diedit, Enter/Tab/panah pindah sel,
// Esc batal, tersimpan otomatis saat pindah sel hanya kalau nilainya berubah.
const KEEP_GRID_COLUMNS = ['bayar', 'net_agent'];

const KeepCurrencyInput = ({ value, onCommit, disabled, gridRow, gridCol }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(Number(value || 0));
  const [saveState, setSaveState] = useState(null); // null | 'saving' | 'error'

  useEffect(() => {
    if (!isEditing) setDraft(Number(value || 0));
  }, [value, isEditing]);

  const commit = async () => {
    setIsEditing(false);
    const nextValue = parseCurrency(draft);
    setDraft(nextValue);
    if (nextValue === Number(value || 0)) return;

    setSaveState('saving');
    try {
      await onCommit(nextValue);
      setSaveState(null);
    } catch (error) {
      setSaveState('error');
      setDraft(Number(value || 0));
    }
  };

  return (
    <div className="keep-currency-cell">
      <Form.Control
        size="sm"
        type="text"
        inputMode="numeric"
        className={`text-end border-0 bg-transparent keep-currency-input${saveState === 'error' ? ' is-error' : ''}`}
        value={isEditing ? (draft ? String(draft) : '') : formatPrice(draft)}
        disabled={disabled}
        data-grid-row={gridRow}
        data-grid-col={gridCol}
        title={saveState === 'error' ? 'Gagal disimpan, coba edit lagi' : undefined}
        onFocus={(event) => {
          const input = event.currentTarget;
          setIsEditing(true);
          setSaveState((current) => (current === 'error' ? null : current));
          window.requestAnimationFrame(() => input.select());
        }}
        onChange={(event) => setDraft(parseCurrency(event.target.value))}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            setDraft(Number(value || 0));
            const input = event.currentTarget;
            window.requestAnimationFrame(() => input.select());
            return;
          }
          handleExcelGridKeyDown(event, KEEP_GRID_COLUMNS);
        }}
      />
      {saveState === 'saving' && <Spinner animation="border" size="sm" className="keep-currency-saving" />}
    </div>
  );
};

const getKeepBayar = (row) => Number(row.bayar || 0);
const getMinusAgent = (row) => Number(row.net_agent || 0) - getKeepBayar(row);

// last_updated dari Accounting V2 (reservation income), bukan last_updated milik record keep.
const getKeepAccountingLastUpdated = (row) =>
  row?.reservation_income_last_updated ??
  row?.reservation_income?.last_updated ??
  row?.accounting_last_updated ??
  row?.income_last_updated;

const getTimeValue = (value) => {
  if (value === undefined || value === null || value === "") return null;
  const time = dayjs(value).valueOf();
  return Number.isNaN(time) ? null : time;
};

// Urutan keep: tanggal transaksi dulu, lalu urutan last_updated Accounting V2
// (yang belum dikerjakan di akunting ditaruh di bawah pada tanggal yang sama).
const compareKeepRows = (a, b) => {
  const dateA = dayjs(a.reservation_date || a.created_at).format('YYYY-MM-DD');
  const dateB = dayjs(b.reservation_date || b.created_at).format('YYYY-MM-DD');
  if (dateA !== dateB) return dateA < dateB ? -1 : 1;

  const updatedA = getTimeValue(getKeepAccountingLastUpdated(a));
  const updatedB = getTimeValue(getKeepAccountingLastUpdated(b));
  if (updatedA !== updatedB) {
    if (updatedA === null) return 1;
    if (updatedB === null) return -1;
    return updatedA - updatedB;
  }

  return (getTimeValue(a.created_at) ?? 0) - (getTimeValue(b.created_at) ?? 0) || (Number(a.id) || 0) - (Number(b.id) || 0);
};

const EXCEL_NUMBER_FORMAT = '#,##0';

// Export Excel mengikuti tampilan halaman: per bulan, urutan yang sama, lengkap dengan total.
const exportKeepToExcel = ({ masterName, startDate, endDate, groupedByMonth }) => {
  const rows = [
    [`DATA KEEP - ${String(masterName || '').toUpperCase()}`],
    [`Periode: ${dayjs(startDate).format('DD MMM YYYY')} - ${dayjs(endDate).format('DD MMM YYYY')}`],
    [],
  ];
  const numberCells = [];
  const pushRow = (row, numberColumns = []) => {
    numberColumns.forEach((col) => numberCells.push({ r: rows.length, c: col }));
    rows.push(row);
  };

  let grandBayar = 0;
  let grandNetAgent = 0;
  let grandMinusAgent = 0;
  let sisaTagihan = 0;

  Object.entries(groupedByMonth).forEach(([monthName, monthData]) => {
    pushRow([`VOUCHERS/MINUS ${monthName}`]);
    pushRow(['NO', 'TANGGAL', 'TRANSAKSI', 'BAYAR', 'NET AGENT', 'MINUS AGENT', 'STATUS']);

    let totalBayar = 0;
    let totalNetAgent = 0;
    let totalMinusAgent = 0;
    monthData.forEach((row, index) => {
      const bayar = getKeepBayar(row);
      const netAgent = Number(row.net_agent || 0);
      const minusAgent = getMinusAgent(row);
      totalBayar += bayar;
      totalNetAgent += netAgent;
      totalMinusAgent += minusAgent;
      if (!row.is_paid) sisaTagihan += Math.max(minusAgent, 0);

      pushRow([
        index + 1,
        dayjs(row.reservation_date).format('DD-MMM-YY').toUpperCase(),
        row.transaksi_text || row.notes || 'Transaksi',
        bayar,
        netAgent,
        minusAgent,
        row.is_paid ? 'PAID' : 'UNPAID',
      ], [3, 4, 5]);
    });

    pushRow(['', '', `TOTAL ${monthName}`, totalBayar, totalNetAgent, totalMinusAgent, ''], [3, 4, 5]);
    pushRow([]);
    grandBayar += totalBayar;
    grandNetAgent += totalNetAgent;
    grandMinusAgent += totalMinusAgent;
  });

  pushRow(['', '', 'GRAND TOTAL', grandBayar, grandNetAgent, grandMinusAgent, ''], [3, 4, 5]);
  pushRow(['', '', 'SISA TAGIHAN (UNPAID)', '', '', sisaTagihan, ''], [5]);

  const worksheet = XLSX.utils.aoa_to_sheet(rows);
  numberCells.forEach(({ r, c }) => {
    const cell = worksheet[XLSX.utils.encode_cell({ r, c })];
    if (cell && cell.t === 'n') cell.z = EXCEL_NUMBER_FORMAT;
  });
  worksheet['!cols'] = [{ wch: 5 }, { wch: 12 }, { wch: 70 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 9 }];

  const workbook = XLSX.utils.book_new();
  const sheetName = `Keep ${masterName || ''}`.replace(/[\\/?*[\]:]/g, '').slice(0, 31) || 'Keep';
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

  // Blob + anchor supaya tetap jalan di WebView Android (sama seperti export lain di app ini).
  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  const safeMasterName = String(masterName || 'master').replace(/[^a-z0-9]+/gi, '_').replace(/^_|_$/g, '');
  link.href = url;
  link.setAttribute('download', `Keep_${safeMasterName}_${startDate}_${endDate}.xlsx`);
  document.body.appendChild(link);
  link.click();
  setTimeout(() => {
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }, 100);
};

const KeepListMonthGroup = ({ monthName, monthData, onRelease, isReleasing, onRowClick, onKeepFieldChange, onDeleteKeep, onDeleteSelected }) => {
  const [selectedIds, setSelectedIds] = useState([]);
  const [paymentInput, setPaymentInput] = useState("");
  const [isDraggingSelection, setIsDraggingSelection] = useState(false);
  const [selectionAnchorIndex, setSelectionAnchorIndex] = useState(null);

  const selectedTotal = useMemo(() => {
    return monthData.filter(t => selectedIds.includes(t.id)).reduce((sum, t) => sum + Math.max(getMinusAgent(t), 0), 0);
  }, [monthData, selectedIds]);

  const selectedReleaseIds = useMemo(() => {
    return monthData
      .filter((row) => selectedIds.includes(row.id) && !row.is_paid && getMinusAgent(row) > 0)
      .map((row) => row.id);
  }, [monthData, selectedIds]);

  const paymentAmount = Number(paymentInput.replace(/[^0-9]/g, '')) || 0;
  const remainingBalance = paymentAmount - selectedTotal;
  const isSufficient = remainingBalance >= 0;

  const handlePaymentInputChange = (e) => {
    const rawVal = e.target.value.replace(/[^0-9]/g, '');
    setPaymentInput(rawVal);
    const amount = Number(rawVal) || 0;
    
    let currentSum = 0;
    const newSelected = [];
    
    for (let t of monthData) {
      if (!t.is_paid) {
        const netValue = Math.max(getMinusAgent(t), 0);
        if (netValue > 0 && currentSum + netValue <= amount) {
          currentSum += netValue;
          newSelected.push(t.id);
        } else if (netValue <= 0) {
           // Auto skip/include zero amounts logic? Just include if not paid.
           // newSelected.push(t.id);
        } else {
          break;
        }
      }
    }
    setSelectedIds(newSelected);
  };

  const getSelectableIdsInRange = useCallback((fromIndex, toIndex) => {
    const start = Math.min(fromIndex, toIndex);
    const end = Math.max(fromIndex, toIndex);

    return monthData
      .slice(start, end + 1)
      .map((row) => row.id);
  }, [monthData]);

  // Pilih seperti Accounting V2: klik / seret MENAMBAH ke pilihan yang sudah ada (tidak mereset).
  // Mode ditentukan dari baris pertama: kalau baris itu sudah terpilih, seret = batal pilih.
  // Pilihan awal disimpan saat mulai seret, jadi seret mundur juga bisa mengurangi rentang dengan mulus.
  const dragSelectionRef = useRef(null);

  const applyDragRange = useCallback((fromIndex, toIndex) => {
    const drag = dragSelectionRef.current;
    if (!drag) return;
    const rangeIds = getSelectableIdsInRange(fromIndex, toIndex);
    const next = new Set(drag.baseIds);
    rangeIds.forEach((id) => {
      if (drag.mode === "deselect") next.delete(id);
      else next.add(id);
    });
    // Urutkan sesuai urutan tabel supaya hasil release / total konsisten.
    setSelectedIds(monthData.map((row) => row.id).filter((id) => next.has(id)));
  }, [getSelectableIdsInRange, monthData]);

  const handleSelectionStart = (event, index, row) => {
    if (isReleasing || !row?.id) return;
    event.preventDefault();
    dragSelectionRef.current = {
      baseIds: new Set(selectedIds),
      mode: selectedIds.includes(row.id) ? "deselect" : "select",
    };
    setIsDraggingSelection(true);
    setSelectionAnchorIndex(index);
    applyDragRange(index, index);
  };

  const handleSelectionEnter = (index) => {
    if (!isDraggingSelection || selectionAnchorIndex === null) return;
    applyDragRange(selectionAnchorIndex, index);
  };

  useEffect(() => {
    const stopDragging = () => {
      setIsDraggingSelection(false);
      dragSelectionRef.current = null;
    };
    window.addEventListener('mouseup', stopDragging);
    return () => window.removeEventListener('mouseup', stopDragging);
  }, []);

  const processRelease = () => {
    if (selectedReleaseIds.length === 0) {
      Swal.fire("Pemberitahuan", "Tidak ada data terpilih yang masih memiliki sisa tagihan.", "warning");
      return;
    }
    if (!isSufficient && paymentAmount > 0) {
      Swal.fire("Pemberitahuan", 'Nominal pembayaran kurang dari total tagihan yang dipilih!', "warning");
      return;
    }
    onRelease(selectedReleaseIds, paymentAmount, monthName);
    setSelectedIds([]);
    setPaymentInput("");
  };

  const processDeleteSelected = async () => {
    if (selectedIds.length === 0) return;
    const deleted = await onDeleteSelected(selectedIds, monthName);
    if (deleted) {
      setSelectedIds([]);
      setPaymentInput("");
    }
  };

  if (monthData.length === 0) return null;

  return (
    <Card className="shadow-sm border-0 mb-4 pb-1">
      <div className="d-flex justify-content-between align-items-center bg-light p-3 border-bottom">
        <h5 className="fw-bold m-0" style={{ color: '#000' }}>{monthName}</h5>
        
        <div className="d-flex align-items-center gap-3">
          <div className="d-flex align-items-center gap-2">
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip>Sistem akan memotong tagihan secara berurutan dari atas ke bawah.</Tooltip>}
            >
              <span className="small fw-bold text-muted whitespace-nowrap" style={{ cursor: 'help', borderBottom: '1px dashed #ccc' }}>
                Input Nominal <i className="fe fe-info text-primary"></i> :
              </span>
            </OverlayTrigger>
            <Form.Control
              type="text"
              size="sm"
              placeholder="Contoh: 10.000.000"
              value={paymentInput === "" ? "" : Number(paymentInput).toLocaleString('id-ID')}
              onChange={handlePaymentInputChange}
              className="fw-bold text-primary"
              style={{ width: '150px' }}
              disabled={isReleasing}
            />
          </div>
          <span className="small text-muted d-none d-xl-inline">
            Drag kolom pilih untuk blok data seperti Excel
          </span>
          
          <div className="d-flex flex-column align-items-end" style={{ minWidth: '150px' }}>
            <span className="fw-bold text-dark" style={{ fontSize: '1.25rem', lineHeight: 1.2 }}>
              Total Terpilih: {formatPrice(selectedTotal)}
            </span>
            {paymentAmount > 0 && (
              <span className={`small fw-bold ${isSufficient ? 'text-success' : 'text-danger'}`}>
                {isSufficient ? `Sisa: ${formatPrice(remainingBalance)}` : `Kurang: ${formatPrice(Math.abs(remainingBalance))}`}
              </span>
            )}
          </div>

          <Button 
            variant="success" 
            size="sm" 
            onClick={processRelease} 
            disabled={(!isSufficient && paymentAmount > 0) || selectedReleaseIds.length === 0 || isReleasing}
          >
            {isReleasing ? <Spinner size="sm" animation="border" /> : <><i className="fe fe-check-circle me-1"></i> Selesaikan</>}
          </Button>
          <Button
            variant="outline-danger"
            size="sm"
            onClick={processDeleteSelected}
            disabled={selectedIds.length === 0 || isReleasing}
          >
            <i className="fe fe-trash-2 me-1"></i> Hapus Terpilih ({selectedIds.length})
          </Button>
        </div>
      </div>

      <div style={{ overflowX: 'auto' }} data-excel-grid>
        <Table size="sm" bordered hover responsive style={{ minWidth: '1200px', margin: 0, border: '2px solid #000', fontSize: '12px', whiteSpace: 'nowrap' }}>
          <thead>
            <tr style={{ backgroundColor: '#ffdd99' }}>
              <th
                className="text-center border-dark"
                style={{ width: '48px', backgroundColor: '#ffcc66' }}
                title="Drag cell di kolom ini untuk memilih blok data"
              >
                PILIH
              </th>
              <th colSpan={2} className="text-black fw-bold border-dark align-middle px-2 py-1" style={{ backgroundColor: '#ffcc66', fontSize: '14px' }}>
                VOUCHERS/MINUS {monthName}
              </th>
              <th className="text-center border-dark" style={{ width: '120px' }}>BAYAR</th>
              <th className="text-center border-dark" style={{ width: '120px' }}>NET AGENT</th>
              <th className="text-center border-dark text-danger" style={{ width: '120px', backgroundColor: '#f4cccc' }}>MINUS AGENT</th>
              <th className="text-center border-dark" style={{ width: '70px' }}>AKSI</th>
            </tr>
          </thead>
          <tbody>
            {monthData.map((row, index) => {
              const minusAgent = getMinusAgent(row);
              const isSelected = selectedIds.includes(row.id);
              const canSelect = Boolean(row.id);
              return (
              <tr key={row.id} style={{ backgroundColor: isSelected ? '#fff3cd' : 'transparent' }}>
                <td
                  className="text-center border-dark align-middle px-2 py-1"
                  onMouseDown={(event) => handleSelectionStart(event, index, row)}
                  onMouseEnter={() => handleSelectionEnter(index)}
                  title={canSelect ? 'Klik lalu drag ke bawah/atas untuk memilih blok data' : ''}
                  style={{
                    cursor: canSelect ? 'crosshair' : 'default',
                    userSelect: 'none',
                    backgroundColor: isSelected ? '#2563eb' : canSelect ? '#f8fbff' : undefined,
                    color: isSelected ? '#fff' : undefined,
                    fontWeight: isSelected ? 800 : 700,
                  }}
                >
                  {row.is_paid ? (
                    <Badge bg="success"><i className="fe fe-check"></i></Badge>
                  ) : canSelect ? (
                    isSelected ? <i className="fe fe-check"></i> : index + 1
                  ) : (
                    "-"
                  )}
                </td>
                <td className="fw-bold text-center border-dark align-middle px-2 py-1" style={{ backgroundColor: '#005580', color: 'white', width: '100px' }}>
                  {dayjs(row.reservation_date).format('DD-MMM-YY').toUpperCase()}
                </td>
                <td className="border-dark text-primary align-middle px-2 py-1 fw-bold" style={{ cursor: "pointer", textDecoration: "underline" }} onClick={() => onRowClick && onRowClick(row.id)}>
                  {row.transaksi_text || row.notes || "Transaksi"}
                </td>
                <td className="text-end border-dark align-middle px-2 py-1">
                  <KeepCurrencyInput
                    value={getKeepBayar(row)}
                    onCommit={(value) => onKeepFieldChange(row, 'bayar', value)}
                    gridRow={index}
                    gridCol="bayar"
                  />
                </td>
                <td className="text-end border-dark align-middle px-2 py-1">
                  <KeepCurrencyInput
                    value={row.net_agent || 0}
                    onCommit={(value) => onKeepFieldChange(row, 'net_agent', value)}
                    gridRow={index}
                    gridCol="net_agent"
                  />
                </td>
                <td
                  className={`text-end fw-bold border-dark align-middle px-2 py-1 ${minusAgent > 0 ? 'text-danger' : minusAgent < 0 ? 'text-warning' : 'text-success'}`}
                  style={{ backgroundColor: minusAgent > 0 ? '#fde9e7' : minusAgent < 0 ? '#fff2cc' : '#e2f0d9' }}
                >
                  {formatPrice(minusAgent)}
                </td>
                <td className="text-center border-dark align-middle px-2 py-1">
                  <Button variant="link" size="sm" className="text-danger p-0" onClick={() => onDeleteKeep(row)}>
                    Hapus
                  </Button>
                </td>
              </tr>
              );
            })}
            
            {(() => {
              const totalMinusAgent = monthData.reduce((s, r) => s + getMinusAgent(r), 0);
              return (
                <tr className="fw-bold text-black border-dark" style={{ backgroundColor: '#ffe6b3' }}>
                  <td colSpan={3} className="text-center border-dark">TOTAL {monthName} (SEMUA DATA)</td>
                  <td className="text-end border-dark">{formatPrice(monthData.reduce((s, r) => s + getKeepBayar(r), 0))}</td>
                  <td className="text-end border-dark">{formatPrice(monthData.reduce((s, r) => s + Number(r.net_agent || 0), 0))}</td>
                  <td
                    className={`text-end border-dark ${totalMinusAgent > 0 ? 'text-danger' : totalMinusAgent < 0 ? 'text-warning' : 'text-success'}`}
                    style={{ backgroundColor: totalMinusAgent > 0 ? '#f4cccc' : totalMinusAgent < 0 ? '#fff2cc' : '#d9ead3' }}
                  >
                    {formatPrice(totalMinusAgent)}
                  </td>
                  <td className="border-dark"></td>
                </tr>
              );
            })()}
          </tbody>
        </Table>
      </div>
    </Card>
  );
};

// Master keep & periode terakhir disimpan di URL (?master=&start=&end=) dan localStorage,
// supaya setelah refresh / kembali ke halaman ini posisinya tetap (mis. tetap di master JOSH).
const KEEP_LAST_VIEW_KEY = "accounting-keep:last-view";
const isDateParam = (value) => /^\d{4}-\d{2}-\d{2}$/.test(value || "");

const readStoredKeepView = () => {
  try {
    return JSON.parse(window.localStorage.getItem(KEEP_LAST_VIEW_KEY) || "null") || {};
  } catch (error) {
    return {};
  }
};

const getInitialKeepView = (searchParams) => {
  const stored = readStoredKeepView();
  const start = searchParams.get("start") || stored.start;
  const end = searchParams.get("end") || stored.end;
  return {
    masterId: searchParams.get("master") || stored.master || null,
    filters: isDateParam(start) && isDateParam(end)
      ? { startDate: start, endDate: end }
      : {
        startDate: dayjs().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
        endDate: dayjs().endOf('month').format('YYYY-MM-DD'),
      },
  };
};

const KeepListPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [initialView] = useState(() => getInitialKeepView(searchParams));
  const [activeMasterId, setActiveMasterId] = useState(null);
  const [masterKeepList, setMasterKeepList] = useState([]);
  const [masterKeepSearch, setMasterKeepSearch] = useState("");
  const [deletingMasterId, setDeletingMasterId] = useState(null);
  const [keepData, setKeepData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isReleasing, setIsReleasing] = useState(false);
  
  const [filters, setFilters] = useState(initialView.filters);

  // Fetch Master Keep (bottom tabs); buka master terakhir dari URL / localStorage kalau masih ada.
  useEffect(() => {
    getAllMasterKeep()
      .then(res => {
        if (res.success && res.data && res.data.length > 0) {
          setMasterKeepList(res.data);
          const savedMaster = res.data.find((master) => String(master.id) === String(initialView.masterId));
          setActiveMasterId((savedMaster || res.data[0]).id);
        }
      })
      .catch(err => console.error("Error fetching master keeps:", err));
  }, [initialView.masterId]);

  useEffect(() => {
    if (!activeMasterId) return;
    const view = { master: String(activeMasterId), start: filters.startDate, end: filters.endDate };
    setSearchParams(view, { replace: true });
    try {
      window.localStorage.setItem(KEEP_LAST_VIEW_KEY, JSON.stringify(view));
    } catch (error) {
      // localStorage tidak tersedia (mis. private mode); URL param tetap jalan.
    }
  }, [activeMasterId, filters.startDate, filters.endDate, setSearchParams]);

  const fetchKeepTransactions = useCallback(async () => {
    if (!activeMasterId) return;
    setIsLoading(true);
    try {
      const q = buildQueryString({
        master_keep_id: activeMasterId,
        start_date: filters.startDate,
        end_date: filters.endDate,
        all: 1
      });
      const res = await getRequest(`/keep${q}`);
      if (res.success && res.data) {
        setKeepData(res.data);
      } else {
        setKeepData([]);
      }
    } catch (err) {
      console.error(err);
      setKeepData([]);
    } finally {
      setIsLoading(false);
    }
  }, [activeMasterId, filters]);

  useEffect(() => {
    fetchKeepTransactions();
  }, [fetchKeepTransactions]);

  const activeMaster = masterKeepList.find(m => m.id === activeMasterId) || {};

  // Hapus master keep langsung dari tab bawah. Kalau master masih punya data keep,
  // admin harus mengetik nama master-nya dulu supaya tidak terhapus karena salah klik.
  const handleDeleteMasterKeep = async (master) => {
    if (!master?.id || deletingMasterId) return;

    let keepCount = null;
    try {
      const res = await getRequest(`/keep${buildQueryString({ master_keep_id: master.id, limit: 1 })}`);
      keepCount = Number(res?.pagination?.total ?? (Array.isArray(res?.data) ? res.data.length : 0)) || 0;
    } catch (error) {
      keepCount = null;
    }

    const masterName = String(master.name || "").trim();
    const hasKeepData = keepCount === null || keepCount > 0;
    const confirmation = await Swal.fire({
      icon: "warning",
      title: `Hapus master keep "${masterName.toUpperCase()}"?`,
      html: keepCount === null
        ? "Jumlah data keep untuk master ini tidak bisa dicek. Pastikan tidak ada transaksi keep yang masih dipakai."
        : keepCount > 0
          ? `Master ini masih punya <b>${keepCount} data keep</b>. Data tersebut bisa ikut terhapus / kehilangan PIC-nya.`
          : "Master ini belum punya data keep.",
      ...(hasKeepData && {
        input: "text",
        inputLabel: `Ketik "${masterName}" untuk konfirmasi`,
        inputPlaceholder: masterName,
        inputValidator: (value) => (
          String(value || "").trim().toLowerCase() === masterName.toLowerCase() ? undefined : "Nama tidak sesuai"
        ),
      }),
      showCancelButton: true,
      confirmButtonText: "Ya, hapus",
      cancelButtonText: "Batal",
      confirmButtonColor: "#dc3545",
    });
    if (!confirmation.isConfirmed) return;

    setDeletingMasterId(master.id);
    try {
      const res = await deleteRequest(`/master-keep/${master.id}`);
      if (res && res.success === false) throw new Error(res.message);

      const remaining = masterKeepList.filter((item) => item.id !== master.id);
      setMasterKeepList(remaining);
      if (activeMasterId === master.id) {
        // Pindah ke tab tetangga supaya admin tidak kehilangan posisi.
        const deletedIndex = masterKeepList.findIndex((item) => item.id === master.id);
        const nextMaster = remaining[Math.min(deletedIndex, remaining.length - 1)] || null;
        setActiveMasterId(nextMaster?.id || null);
        if (!nextMaster) setKeepData([]);
      }
      Swal.fire({ icon: "success", title: "Master keep dihapus", timer: 1400, showConfirmButton: false });
    } catch (error) {
      Swal.fire("Gagal", error?.message || "Gagal menghapus master keep.", "error");
    } finally {
      setDeletingMasterId(null);
    }
  };

  const filteredMasterKeepList = useMemo(() => {
    const keyword = masterKeepSearch.trim().toLowerCase();
    if (!keyword) return masterKeepList;
    return masterKeepList.filter((master) => String(master.name || "").toLowerCase().includes(keyword));
  }, [masterKeepList, masterKeepSearch]);

  // Pastikan tab aktif selalu terlihat di bar bawah (misalnya setelah pilih dari hasil pencarian).
  useEffect(() => {
    if (!activeMasterId) return;
    document
      .querySelector(`[data-master-keep-tab="${activeMasterId}"]`)
      ?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [activeMasterId, filteredMasterKeepList]);

  // Grouping by Month
  const groupedByMonth = useMemo(() => {
    const groups = {};
    keepData.forEach(item => {
      const monthName = dayjs(item.reservation_date).format('MMMM YYYY').toUpperCase();
      if (!groups[monthName]) groups[monthName] = [];
      groups[monthName].push(item);
    });
    
    Object.keys(groups).forEach(key => {
      groups[key].sort(compareKeepRows);
    });
    
    return groups;
  }, [keepData]);

  const handleExportExcel = () => {
    if (keepData.length === 0) {
      Swal.fire("Pemberitahuan", "Tidak ada data keep untuk diexport.", "warning");
      return;
    }
    try {
      exportKeepToExcel({
        masterName: activeMaster.name,
        startDate: filters.startDate,
        endDate: filters.endDate,
        groupedByMonth,
      });
    } catch (error) {
      console.error(error);
      Swal.fire("Gagal", "Export Excel gagal diproses.", "error");
    }
  };

  const [processProgress, setProcessProgress] = useState({ active: false, current: 0, total: 100, label: "" });

  const handleRelease = async (selectedIds, paymentAmount, monthName) => {
    if (selectedIds.length === 0) return;
    setIsReleasing(true);
    setProcessProgress({ active: true, current: 0, total: 100, label: "Memproses Pembayaran Keep..." });
    
    // Simulate progress
    let simProgress = 0;
    const progressInterval = setInterval(() => {
      simProgress += Math.random() * 15;
      if (simProgress > 90) simProgress = 90;
      setProcessProgress(prev => ({ ...prev, current: Math.round(simProgress) }));
    }, 300);

    try {
      const res = await postRequest('/keep/release', {
        total_paid: paymentAmount,
        keep_ids: selectedIds
      });
      
      clearInterval(progressInterval);
      if (res.success) {
        setProcessProgress(prev => ({ ...prev, current: 100 }));
        await new Promise(r => setTimeout(r, 400));
        Swal.fire("Pemberitahuan", `Berhasil menyelesaikan ${selectedIds.length} transaksi di bulan ${monthName}!`, "success");
        fetchKeepTransactions(); // Refresh data
      } else {
        Swal.fire("Pemberitahuan", res.message || "Gagal melakukan rilis tagihan", "error");
      }
    } catch (err) {
      clearInterval(progressInterval);
      Swal.fire("Pemberitahuan", err.message || "Terjadi kesalahan saat memanggil API release", "warning");
    } finally {
      clearInterval(progressInterval);
      setProcessProgress({ active: false, current: 0, total: 100, label: "" });
      setIsReleasing(false);
    }
  };

  const [showDetail, setShowDetail] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const handleOpenDetail = async (id) => {
    setShowDetail(true);
    setDetailLoading(true);
    try {
      const res = await getRequest('/keep/' + id);
      if (res.success && res.data) {
        setDetailData(res.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleKeepFieldChange = async (row, field, value) => {
    if (Number(row[field] || 0) === value) return;
    try {
      await patchRequest(`/keep/${row.id}`, { [field]: value });
      setKeepData((current) => current.map((item) => item.id === row.id ? { ...item, [field]: value } : item));
      setDetailData((current) => current?.id === row.id ? { ...current, [field]: value } : current);
    } catch (err) {
      Swal.fire("Pemberitahuan", err.message || "Gagal mengubah data Keep", "error");
      // Lempar lagi supaya sel menampilkan status gagal dan kembali ke nilai semula.
      throw err;
    }
  };

  const handleDeleteKeep = async (row) => {
    const confirm = await Swal.fire({
      title: "Hapus data keep?",
      text: "Data ini akan dikembalikan dari daftar keep.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Ya, hapus",
      cancelButtonText: "Batal",
    });

    if (!confirm.isConfirmed) return;

    setProcessProgress({ active: true, current: 0, total: 1, label: "Menghapus Data Keep..." });
    try {
      await deleteRequest(`/keep/${row.id}`);
      setProcessProgress(prev => ({ ...prev, current: 1 }));
      await new Promise(r => setTimeout(r, 400));
      setKeepData((current) => current.filter((item) => item.id !== row.id));
      Swal.fire("Pemberitahuan", "Data keep berhasil dihapus", "success");
    } catch (err) {
      Swal.fire("Pemberitahuan", err.message || "Gagal menghapus data keep", "error");
    } finally {
      setProcessProgress({ active: false, current: 0, total: 100, label: "" });
    }
  };

  const handleDeleteSelectedKeep = async (selectedIds, monthName) => {
    const confirm = await Swal.fire({
      title: "Hapus data keep terpilih?",
      text: `${selectedIds.length} data di ${monthName} akan dihapus dari daftar keep.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Ya, hapus semua",
      cancelButtonText: "Batal",
    });

    if (!confirm.isConfirmed) return false;

    // 1 request POST /keep/bulk-delete, atomic: kalau ada yang tidak bisa dihapus (mis. sudah dibayar),
    // tidak ada yang terhapus sama sekali supaya data uang tidak terhapus setengah.
    setProcessProgress({ active: true, current: 0, total: 0, indeterminate: true, label: `Menghapus ${selectedIds.length} Data Keep...` });
    let response;
    try {
      response = await runChunkedBulkRequest({
        url: "/keep/bulk-delete",
        method: "POST",
        items: selectedIds,
        buildPayload: (chunk) => ({ atomic: true, ids: chunk }),
      });
    } catch (err) {
      Swal.fire("Pemberitahuan", err.message || "Gagal menghapus data keep terpilih", "error");
      return false;
    } finally {
      setProcessProgress({ active: false, current: 0, total: 100, label: "" });
    }

    const deletedIds = response.results
      .filter((result) => result.status === "ok" || result.status === "not_found")
      .map((result) => selectedIds[result.index]);
    if (!response.rolledBack) setKeepData((current) => current.filter((item) => !deletedIds.includes(item.id)));

    const failures = getBulkFailures(
      response.results.filter((result) => result.status !== "not_found"),
      selectedIds,
      (id) => keepData.find((item) => item.id === id)?.transaksi_text || id
    );
    if (failures.length === 0) {
      Swal.fire("Pemberitahuan", `${deletedIds.length} data keep berhasil dihapus`, "success");
      return true;
    }
    Swal.fire({
      icon: "warning",
      title: response.rolledBack ? "Dibatalkan: tidak ada data keep yang dihapus" : `${response.okCount} terhapus, ${failures.length} gagal`,
      html: (response.rolledBack ? "<div class='mb-2 small'>Data keep yang sudah dibayar tidak bisa dihapus. Batalkan pilihan baris tersebut lalu coba lagi.</div>" : "")
        + buildBulkFailureHtml(failures.filter((failure) => failure.result.status !== "skipped")),
    });
    return !response.rolledBack && response.okCount > 0;
  };

  return (
    <LayoutDashboard>
      <div className="container-fluid pt-3 pb-5 mb-5">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h4 className="fw-bold m-0 text-uppercase">DATA KEEP - {activeMaster.name || "..."}</h4>
            <div className="text-muted small mt-1">
              Daftar transaksi yang ditangguhkan pembayarannya ke koordinator
            </div>
          </div>
          <div className="bg-white px-4 py-2 rounded shadow-sm border-start border-warning border-4 text-end">
            <div className="text-muted small fw-bold text-uppercase">Sisa Tagihan {activeMaster.name || "..."}</div>
            <h4 className="text-warning fw-bold m-0">
              {formatPrice(keepData.filter(t => !t.is_paid).reduce((s, t) => s + Math.max(getMinusAgent(t), 0), 0))}
            </h4>
          </div>
        </div>

        <Card className="shadow-sm border-0 mb-4 bg-light">
          <Card.Body className="p-3">
            <div className="row g-2 align-items-end">
              <div className="col-md-3">
                <Form.Label className="small fw-bold mb-1">Start Date</Form.Label>
                <Form.Control type="date" size="sm" value={filters.startDate} onChange={e => setFilters({...filters, startDate: e.target.value})} />
              </div>
              <div className="col-md-3">
                <Form.Label className="small fw-bold mb-1">End Date</Form.Label>
                <Form.Control type="date" size="sm" value={filters.endDate} onChange={e => setFilters({...filters, endDate: e.target.value})} />
              </div>
              <div className="col-md-6 d-flex justify-content-md-end">
                <Button
                  variant="success"
                  size="sm"
                  onClick={handleExportExcel}
                  disabled={isLoading || keepData.length === 0}
                  title={keepData.length === 0 ? 'Tidak ada data untuk diexport' : 'Export data keep sesuai master & periode terpilih'}
                >
                  <i className="fe fe-download me-1"></i> Export Excel
                </Button>
              </div>
            </div>
          </Card.Body>
        </Card>

        {isLoading ? (
          <div className="text-center p-5 bg-light rounded border border-dashed">
            <Spinner animation="border" variant="primary" />
            <div className="mt-2 text-muted">Memuat data transaksi...</div>
          </div>
        ) : Object.keys(groupedByMonth).length === 0 ? (
           <div className="text-center text-muted p-5 bg-light rounded border border-dashed">
             Tidak ada transaksi Keep untuk PIC ini pada periode terpilih.
           </div>
        ) : (
          Object.keys(groupedByMonth).map(monthName => (
            <KeepListMonthGroup onRowClick={handleOpenDetail} 
              key={monthName}
              monthName={monthName}
              monthData={groupedByMonth[monthName]}
              onRelease={handleRelease}
              isReleasing={isReleasing}
              onKeepFieldChange={handleKeepFieldChange}
              onDeleteKeep={handleDeleteKeep}
              onDeleteSelected={handleDeleteSelectedKeep}
            />
          ))
        )}

        {/* Bottom Tab Master Keep */}
        <div 
          className="d-flex align-items-center bg-light shadow-lg" 
          style={{ 
            position: 'fixed', 
            bottom: 0, left: 0, right: 0, zIndex: 1000, 
            overflowX: 'auto', borderTop: '2px solid #ccc'
          }}
        >
          <div
            className="d-flex align-items-center gap-2"
            style={{ position: 'sticky', left: 0, zIndex: 1, minWidth: '340px', borderRight: '1px solid #ccc', padding: '6px 12px', backgroundColor: '#e2e3e5', fontWeight: 'bold' }}
          >
            <span className="text-nowrap">MASTER KEEP</span>
            <Form.Control
              size="sm"
              type="search"
              placeholder={`Cari dari ${masterKeepList.length} master keep...`}
              value={masterKeepSearch}
              onChange={(event) => setMasterKeepSearch(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && filteredMasterKeepList.length > 0) {
                  event.preventDefault();
                  setActiveMasterId(filteredMasterKeepList[0].id);
                }
                if (event.key === 'Escape') setMasterKeepSearch("");
              }}
              style={{ width: '180px' }}
              aria-label="Cari master keep"
            />
          </div>
          {masterKeepList.length === 0 ? (
             <div className="p-2 text-muted small">Loading...</div>
          ) : filteredMasterKeepList.length === 0 ? (
             <div className="p-2 px-3 text-muted small text-nowrap">Master keep "{masterKeepSearch}" tidak ditemukan</div>
          ) : (
            filteredMasterKeepList.map((master) => (
              <div 
                key={master.id}
                data-master-keep-tab={master.id}
                className={`keep-master-tab ${activeMasterId === master.id ? 'active' : ''}`}
                onClick={() => setActiveMasterId(master.id)}
                style={{
                  position: 'relative',
                  padding: '10px 28px 10px 20px', cursor: 'pointer',
                  opacity: deletingMasterId === master.id ? 0.5 : 1,
                  fontWeight: activeMasterId === master.id ? 'bold' : 'normal',
                  backgroundColor: activeMasterId === master.id ? '#fff' : '#f8f9fa',
                  borderRight: '1px solid #ccc',
                  borderBottom: activeMasterId === master.id ? '3px solid #ff9900' : '3px solid transparent',
                  minWidth: '120px', textAlign: 'center', whiteSpace: 'nowrap'
                }}
              >
                {master.name.toUpperCase()}
                <button
                  type="button"
                  className="keep-master-tab-delete"
                  title={`Hapus master keep ${master.name}`}
                  aria-label={`Hapus master keep ${master.name}`}
                  disabled={Boolean(deletingMasterId)}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleDeleteMasterKeep(master);
                  }}
                >
                  {deletingMasterId === master.id ? <Spinner animation="border" size="sm" style={{ width: 10, height: 10, borderWidth: 2 }} /> : '×'}
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    
      <Offcanvas show={showDetail} onHide={() => setShowDetail(false)} placement="end" style={{ width: '400px' }}>
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>Detail Keep</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          {detailLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : detailData ? (
            <div>
              <div className="mb-3">
                <small className="text-muted d-block">ID Transaksi</small>
                <strong>{detailData.id}</strong>
              </div>
              <div className="mb-3">
                <small className="text-muted d-block">Status</small>
                {detailData.is_paid ? <Badge bg="success">Paid</Badge> : <Badge bg="warning">Unpaid</Badge>}
              </div>
              <div className="mb-3">
                <small className="text-muted d-block">Tamu</small>
                <strong>{detailData.reservation_income?.reservation?.guest_name || '-'}</strong>
              </div>
              <div className="mb-3">
                <small className="text-muted d-block">Track</small>
                <strong>{detailData.reservation_income?.reservation?.track || '-'}</strong>
              </div>
              <div className="mb-3">
                <small className="text-muted d-block">Host / Creator</small>
                <strong>{detailData.creator?.name || '-'}</strong>
              </div>
              <div className="mb-3">
                <small className="text-muted d-block">Koordinator (Master Keep)</small>
                <strong>{detailData.master_keep?.name || '-'}</strong>
              </div>
              <div className="mb-3">
                <small className="text-muted d-block">Agent</small>
                <strong>{detailData.agent?.name || '-'}</strong>
              </div>
              <div className="mb-3">
                <small className="text-muted d-block">Tanggal Transaksi</small>
                <strong>{dayjs(detailData.created_at).format('DD MMM YYYY HH:mm')}</strong>
              </div>
              <hr/>
              <div className="mb-2 d-flex justify-content-between">
                <span>Payment Bill:</span>
                <strong>{formatPrice(Number(detailData.payment_bill || 0))}</strong>
              </div>
              <div className="mb-2 d-flex justify-content-between">
                <span>Net Agent:</span>
                <strong>{formatPrice(Number(detailData.net_agent || 0))}</strong>
              </div>

              <div className="mb-2 d-flex justify-content-between text-success">
                <span>Bayar:</span>
                <strong>{formatPrice(Number(detailData.bayar || 0))}</strong>
              </div>
              {(() => {
                const minusAgent = getMinusAgent(detailData);
                return (
                  <div className={`mb-2 d-flex justify-content-between ${minusAgent > 0 ? 'text-danger' : minusAgent < 0 ? 'text-warning' : 'text-success'}`}>
                    <span>Minus Agent:</span>
                    <strong>{formatPrice(minusAgent)}</strong>
                  </div>
                );
              })()}
              
              <hr/>
              <h6 className="fw-bold">Detail Reservasi</h6>
              <div className="mb-2 d-flex justify-content-between">
                <span>Kode:</span>
                <strong>{detailData.reservation_income?.reservation?.code || '-'}</strong>
              </div>
              <div className="mb-2 d-flex justify-content-between">
                <span>Tgl Reservasi:</span>
                <strong>{detailData.reservation_income?.reservation?.reservation_date || '-'}</strong>
              </div>
              <div className="mb-2 d-flex justify-content-between">
                <span>Guest Pax:</span>
                <strong>{detailData.reservation_income?.reservation?.guest_pax || '-'} Pax</strong>
              </div>

              <hr/>
              <h6 className="fw-bold">Rincian Komisi (Income)</h6>
              {Number(detailData.reservation_income?.susuk_agent || 0) > 0 && (
                <div className="mb-2 d-flex justify-content-between text-danger">
                  <span>Susuk Agent:</span>
                  <strong>{formatPrice(Number(detailData.reservation_income?.susuk_agent))}</strong>
                </div>
              )}
              {Number(detailData.reservation_income?.net_kuber_mm || 0) > 0 && (
                <div className="mb-2 d-flex justify-content-between text-primary">
                  <span>Net Kuber MM:</span>
                  <strong>{formatPrice(Number(detailData.reservation_income?.net_kuber_mm))}</strong>
                </div>
              )}
              {Number(detailData.reservation_income?.net_kuber_nam || 0) > 0 && (
                <div className="mb-2 d-flex justify-content-between text-primary">
                  <span>Net Kuber NAM:</span>
                  <strong>{formatPrice(Number(detailData.reservation_income?.net_kuber_nam))}</strong>
                </div>
              )}
              {Number(detailData.reservation_income?.net_kuber_bm || 0) > 0 && (
                <div className="mb-2 d-flex justify-content-between text-primary">
                  <span>Net Kuber BM:</span>
                  <strong>{formatPrice(Number(detailData.reservation_income?.net_kuber_bm))}</strong>
                </div>
              )}
              {Number(detailData.reservation_income?.net_kuber_josh || 0) > 0 && (
                <div className="mb-2 d-flex justify-content-between text-primary">
                  <span>Net Kuber JOSH:</span>
                  <strong>{formatPrice(Number(detailData.reservation_income?.net_kuber_josh))}</strong>
                </div>
              )}
              {Number(detailData.reservation_income?.others || 0) > 0 && (
                <div className="mb-2 d-flex justify-content-between text-secondary">
                  <span>Lainnya (Others):</span>
                  <strong>{formatPrice(Number(detailData.reservation_income?.others))}</strong>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-5 text-muted">Data tidak ditemukan</div>
          )}
        </Offcanvas.Body>
      </Offcanvas>
      <Modal show={processProgress.active} centered backdrop="static" keyboard={false}>
        <Modal.Body>
          <div className="upload-animation-container p-4 text-center">
            <i className="fe fe-upload-cloud upload-icon-bounce text-success" style={{ fontSize: '48px', animation: 'bounceUpload 1.5s infinite' }}></i>
            <h5 className="mt-3">{processProgress.label}</h5>
            <div style={{ width: "80%", margin: "20px auto" }}>
              {processProgress.indeterminate ? (
                <ProgressBar animated striped variant="success" now={100} />
              ) : (
                <>
                  <ProgressBar animated variant="success" now={Math.round((processProgress.current / processProgress.total) * 100) || 0} />
                  <div className="fw-bold mt-2">{Math.round((processProgress.current / processProgress.total) * 100) || 0}%</div>
                </>
              )}
            </div>
            <p className="text-muted small">Harap tunggu, proses sedang berjalan.</p>
          </div>
        </Modal.Body>
      </Modal>
    </LayoutDashboard>
  );
};

export default KeepListPage;
